/**
 * Builds a single SVG <path> `d` string that is the outline of the union of
 * all given grid tiles — no interior dividing lines, any shape supported.
 *
 * Algorithm:
 *   1. Collect all directed boundary half-edges: for each tile, add the four
 *      cardinal edges that do NOT have a neighbouring tile on the other side.
 *      Direction is chosen so the tile interior is always to the LEFT of the
 *      edge direction (standard CCW winding, which in SVG screen-coords reads
 *      as a clockwise exterior — SVG fill does not care about winding).
 *   2. Trace the edges head-to-tail into one or more closed loops.
 *      At concave vertices where two edges meet, a right-hand-turn preference
 *      keeps exterior and interior loops separate.
 *   3. Convert loops to M…L…Z path segments.
 *
 * @param  {Array} tiles   Tile definitions from buildGrid — each must have
 *                          .col, .row, .x, .y, .w, .h (all numbers).
 * @returns {string}       SVG path `d` value.
 */
export function buildUnionPath( tiles ) {
	if ( ! tiles || tiles.length === 0 ) return '';

	// ── 1. Collect boundary half-edges ────────────────────────────────────────
	const tileSet = new Set( tiles.map( ( t ) => `${ t.col },${ t.row }` ) );
	const edges   = []; // each: [x1, y1, x2, y2]

	for ( const t of tiles ) {
		const { x, y, w, h, col, row } = t;
		const r = x + w;
		const b = y + h;

		// Top edge → going right  (no tile above)
		if ( ! tileSet.has( `${ col },${ row - 1 }` ) ) edges.push( [ x, y, r, y ] );
		// Right edge → going down  (no tile to the right)
		if ( ! tileSet.has( `${ col + 1 },${ row }` ) ) edges.push( [ r, y, r, b ] );
		// Bottom edge → going left (no tile below)
		if ( ! tileSet.has( `${ col },${ row + 1 }` ) ) edges.push( [ r, b, x, b ] );
		// Left edge → going up    (no tile to the left)
		if ( ! tileSet.has( `${ col - 1 },${ row }` ) ) edges.push( [ x, b, x, y ] );
	}

	if ( edges.length === 0 ) return '';

	// ── 2. Build start-point lookup  (point → [edge indices]) ─────────────────
	/** @type {Map<string, number[]>} */
	const fromPoint = new Map();
	edges.forEach( ( edge, idx ) => {
		const key = `${ edge[ 0 ] },${ edge[ 1 ] }`;
		if ( ! fromPoint.has( key ) ) fromPoint.set( key, [] );
		fromPoint.get( key ).push( idx );
	} );

	// ── 3. Trace closed loops ──────────────────────────────────────────────────
	const used  = new Set();
	const loops = [];

	for ( let start = 0; start < edges.length; start++ ) {
		if ( used.has( start ) ) continue;

		const loop   = [];
		let   curIdx = start;

		while ( ! used.has( curIdx ) ) {
			used.add( curIdx );
			const [ x1, y1, x2, y2 ] = edges[ curIdx ];
			loop.push( [ x1, y1 ] );

			// Find next unused edge(s) starting at (x2, y2)
			const candidates = ( fromPoint.get( `${ x2 },${ y2 }` ) ?? [] )
				.filter( ( i ) => ! used.has( i ) );

			if ( candidates.length === 0 ) break;

			if ( candidates.length === 1 ) {
				curIdx = candidates[ 0 ];
			} else {
				// Concave vertex — pick the edge that makes the sharpest right turn
				// (smallest signed cross-product of incoming × outgoing direction).
				const dx_in = x2 - x1;
				const dy_in = y2 - y1;
				curIdx = candidates.reduce( ( best, idx ) => {
					const dx_out_b = edges[ best ][ 2 ] - edges[ best ][ 0 ];
					const dy_out_b = edges[ best ][ 3 ] - edges[ best ][ 1 ];
					const dx_out_c = edges[ idx  ][ 2 ] - edges[ idx  ][ 0 ];
					const dy_out_c = edges[ idx  ][ 3 ] - edges[ idx  ][ 1 ];
					// cross = dx_in * dy_out - dy_in * dx_out  (negative = right turn)
					const cross_b = dx_in * dy_out_b - dy_in * dx_out_b;
					const cross_c = dx_in * dy_out_c - dy_in * dx_out_c;
					return cross_c < cross_b ? idx : best;
				} );
			}
		}

		if ( loop.length >= 3 ) {
			loops.push( loop );
		}
	}

	// ── 4. Convert loops to SVG path string ────────────────────────────────────
	return loops
		.map( ( pts ) =>
			`M ${ pts[ 0 ].join( ' ' ) } ` +
			pts.slice( 1 ).map( ( p ) => `L ${ p.join( ' ' ) }` ).join( ' ' ) +
			' Z'
		)
		.join( ' ' );
}
